import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { Square } from 'lucide-react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useForgetSortItems, useSaveSortPreference } from '@renderer/hooks/use-sort-preferences'
import { placeTodoInPinned, type PinnedTodoDropTarget } from './pinned-todo-drop'
import { usePinnedTodoDrag } from './use-pinned-todo-drag'
import { todoEntryKey, type TodoEntry } from './todos-order'
import { HomeHeading } from '../nested/home-heading'

export const PINNED_TODOS_SORT_KEY = 'pinnedTodos'

const EMPTY_PINNED_HEADING_TRANSITION: Transition = { duration: 0.2, ease: [0.22, 1, 0.36, 1] }
const NO_MOTION: Transition = { duration: 0 }
const EMPTY_PINNED_HEADING_VARIANTS: Variants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1 },
}

interface PinnedTodoReorderContext {
  order: Array<string>
  draggedId: string | null
  startDrag: (event: ReactPointerEvent<HTMLElement>, todo: TodoEntry) => void
  target: PinnedTodoDropTarget | null
  move: (todo: TodoEntry, target: PinnedTodoDropTarget) => void
}

const ReorderContext = createContext<PinnedTodoReorderContext | null>(null)

interface PinnedTodoReorderProps {
  todos: Array<TodoEntry>
  /** The to-dos listed outside Pinned. */
  unpinnedTodos: Array<TodoEntry>
  onPin: (todo: TodoEntry) => void
  onUnpin: (todo: TodoEntry) => void
  children: ReactNode
}

export function PinnedTodoReorder({ todos, unpinnedTodos, onPin, onUnpin, children }: PinnedTodoReorderProps) {
  const [announcement, setAnnouncement] = useState({ text: '', error: false })
  const reduceMotion = useReducedMotion()
  const saveOrder = useSaveSortPreference(PINNED_TODOS_SORT_KEY)
  const order = todos.map(todoEntryKey)
  useForgetUnpinnedOrder(order, unpinnedTodos)

  const move = (todo: TodoEntry, destination: PinnedTodoDropTarget) => {
    const source = todoEntryKey(todo)
    const next = placeTodoInPinned(order, source, destination)
    if (next.length !== order.length || next.some((id, index) => id !== order[index])) {
      const saved = saveOrder(next)
      if (destination.section === 'todos') {
        onUnpin(todo)
      } else if (!order.includes(source)) {
        onPin(todo)
      }
      setAnnouncement({
        text: saved
          ? destination.section === 'todos'
            ? 'Unpinned to-do.'
            : `Moved to position ${next.indexOf(source) + 1} of ${next.length}.`
          : 'Could not save the order. Try again.',
        error: !saved,
      })
    }
  }
  const { listRef, draggedId, target, startDrag, preview, previewX, previewY, suppressClick } = usePinnedTodoDrag(
    order,
    move,
  )

  return (
    <ReorderContext.Provider value={{ order, draggedId, target, startDrag, move }}>
      <div ref={listRef} onClickCapture={suppressClick} className="[overflow-anchor:none]">
        {/* A landed pin unmounts the whole presence, so a collapsing heading never stacks on the real one. */}
        {order.length === 0 && (
          <AnimatePresence>
            {draggedId && (
              <motion.div
                key="empty-pinned-heading"
                className="overflow-hidden"
                variants={EMPTY_PINNED_HEADING_VARIANTS}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={reduceMotion ? NO_MOTION : EMPTY_PINNED_HEADING_TRANSITION}
              >
                <PinnedTodoDropZone>
                  <HomeHeading>Pinned</HomeHeading>
                </PinnedTodoDropZone>
              </motion.div>
            )}
          </AnimatePresence>
        )}
        {children}
      </div>
      {preview &&
        createPortal(
          <motion.div
            aria-hidden="true"
            data-pinned-drag-preview
            className="bg-background-overlay text-foreground corner-smooth pointer-events-none fixed left-0 top-0 z-[100] flex items-center gap-2 rounded-xl px-3 py-1.5 shadow-lg"
            style={{ x: previewX, y: previewY, width: preview.width, minHeight: preview.height }}
          >
            <Square className="text-foreground-secondary size-3.5 shrink-0" />
            <span className="type-ui-md truncate">{preview.title}</span>
          </motion.div>,
          document.body,
        )}
      <span role="status" className={announcement.error ? 'text-danger-subtle type-ui-sm px-3 py-1' : 'sr-only'}>
        {announcement.text}
      </span>
    </ReorderContext.Provider>
  )
}

/**
 * Drops a to-do unpinned from anywhere (its moves, a menu, its popover or a drag) from the saved
 * order, so pinning it again puts it first. Only one seen moving from Pinned into the list counts:
 * one that's completed or no longer loaded keeps its place.
 */
function useForgetUnpinnedOrder(order: Array<string>, unpinnedTodos: Array<TodoEntry>) {
  const forget = useForgetSortItems(PINNED_TODOS_SORT_KEY)
  const previousOrder = useRef(order)
  useEffect(() => {
    const listed = new Set(unpinnedTodos.map(todoEntryKey))
    const unpinned = previousOrder.current.filter((key) => !order.includes(key) && listed.has(key))
    previousOrder.current = order
    if (unpinned.length > 0) forget(unpinned)
  })
}

interface DraggableTodoRowProps {
  todo: TodoEntry
  disabled?: boolean
  children: ReactNode
}

export function DraggableTodoRow({ todo, disabled, children }: DraggableTodoRowProps) {
  const context = useContext(ReorderContext)
  const id = todoEntryKey(todo)
  if (!context || disabled || (todo.kind === 'chat' && todo.chat.stagedActionIds.length === 0)) {
    return children
  }
  const { order, draggedId, target, startDrag, move } = context
  const isPinned = order.includes(id)
  const isTarget = target?.id === id

  return (
    <div
      className={twMerge('relative touch-none', draggedId === id && 'opacity-30')}
      data-draggable-todo={id}
      data-pinned-todo={isPinned ? id : undefined}
      data-todo-drag-title={todo.kind === 'reminder' ? todo.reminder.title : todo.chat.title}
      role="group"
      tabIndex={0}
      aria-label={isPinned ? 'Reorder pinned to-do' : 'Drag to pin to-do'}
      aria-description={
        isPinned
          ? 'Drag to reorder or into To-dos to unpin, or use the up and down arrow keys.'
          : 'Drag into Pinned to pin this to-do.'
      }
      onPointerDown={(event) => {
        if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target)) {
          return
        }
        const control = event.target.closest(
          'button, input, textarea, select, a, [role="checkbox"], [role="menuitem"], [contenteditable="true"], [data-no-drag]',
        )
        if (!control) {
          startDrag(event, todo)
        }
      }}
      onDragStart={(event) => event.preventDefault()}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) {
          return
        }
        // This wrapper is a tab stop ahead of the row it wraps, and the row is
        // what handles Enter and Space. Without forwarding them, tabbing to a
        // to-do and pressing Enter does nothing at all.
        if (event.key === 'Enter' || event.key === ' ') {
          const cell = event.currentTarget.querySelector<HTMLElement>('[data-slot="cell"]')
          if (!cell) {
            return
          }
          event.preventDefault()
          cell.focus()
          cell.click()
          return
        }
        if (!isPinned || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
          return
        }
        event.preventDefault()
        event.stopPropagation()
        const offset = event.key === 'ArrowUp' ? -1 : 1
        const neighbor = order[order.indexOf(id) + offset]
        if (neighbor) {
          move(todo, { id: neighbor, placement: offset < 0 ? 'before' : 'after' })
        }
      }}
    >
      {children}
      {isTarget && (
        <div
          aria-hidden="true"
          data-pinned-drop-indicator={target.placement}
          className={twMerge(
            'pointer-events-none absolute inset-x-3 z-20 flex h-0 items-center',
            target.placement === 'before' ? 'top-0' : 'bottom-0',
          )}
        >
          <span className="bg-info size-1.5 shrink-0 rounded-full" />
          <span className="bg-info h-0.5 flex-1" />
        </div>
      )}
    </div>
  )
}

interface PinnedTodoDropZoneProps {
  children: ReactNode
  section?: 'pinned' | 'todos'
  first?: boolean
  last?: boolean
}

export function PinnedTodoDropZone({
  children,
  section = 'pinned',
  first = true,
  last = true,
}: PinnedTodoDropZoneProps) {
  const context = useContext(ReorderContext)
  const draggedId = context?.draggedId
  const showPinHint = section === 'pinned' && draggedId != null && !context?.order.includes(draggedId)
  const active = context?.target != null && (context.target.section ?? 'pinned') === section
  return (
    <div
      data-pinned-drop-zone={section === 'pinned' ? '' : undefined}
      data-todos-drop-zone={section === 'todos' ? '' : undefined}
      className="relative"
    >
      {children}
      {(showPinHint || (section === 'todos' && active)) && (
        <div
          aria-hidden="true"
          className={twMerge(
            'border-x-1 pointer-events-none absolute inset-0 z-10',
            active ? 'border-info border-x-2' : 'border-strong',
            first && 'border-t-1 top-2 rounded-t-lg',
            first && active && 'border-t-2',
            last && 'border-b-1 rounded-b-lg',
            last && active && 'border-b-2',
          )}
        />
      )}
    </div>
  )
}
